import { assert } from 'ts-essentials';
import { STTLTimeNodeType } from '@tencent/slide-sdk-pb-model';
import { K_DEFAULT_TRIGGER_ID, MEDIA_TRIGGER_ID_PREFIX } from '../constants/timing-constants';
import { SHAPE_DATA_TYPES } from '../data';
import { UnitTimeNode } from '../model/unit-time-node';
import { MoveAnimationMutation, SetAnimationMutation, getMediaParams } from '../mutation/timing';
import { AddAnimationMutation } from '../mutation/timing/add-animation-mutation';
import { RemoveAnimationMutation } from '../mutation/timing/remove-animation-mutation';
import { record } from '../utils/recorder';
import { cloneDeep } from '../utils/clone-deep';
import { MutationAPI } from './mutation-api';
import type { IEventManager } from '../manager/event-manager';
import type { DeepPartial, DeepReadonly } from 'ts-essentials';
import type { IAnimModifyParam, IAddAnimationParam, IOriginMediaAnimModifyParam } from '../constants/timing-constants';
import type { PageContext } from '../context/page-context';
import type { TimingDataModel } from '../model/timing-data-model';
import type { IEGTimeNodeChoice, ICTTLMediaNodeAudio, ICTTLMediaNodeVideo } from '@tencent/slide-sdk-pb-model';

export class TimingMutationApi extends MutationAPI<TimingDataModel> {
    public constructor(model: TimingDataModel, events: IEventManager, private context: PageContext) {
        super(model, events);
    }

    /**
     * 添加动画
     * @param shapeIds 添加动画的shapes
     * @param animType 具体动画类型
     * @param triggerId 添加动画所属队列的触发器元素 id
     * @param index 添加动画在队列中的索引
     * @param presetSubtype 预设动画方向
     * @param mediaDuration 视频持续时间
     */
    @record()
    public addAnimation(addAnimationParam: IAddAnimationParam) {
        const {
            index,
            delay,
            shapeIds,
            animType,
            triggerId,
            presetSubtype,
            mediaDuration: duration,
            mediaType,
        } = addAnimationParam;
        let { nodeType } = addAnimationParam;
        const validShapeIds = shapeIds.filter((shapeId) =>
            this.getPresentation().store.models.get(shapeId, ...SHAPE_DATA_TYPES),
        );
        this.transaction(() => {
            validShapeIds.forEach((shapeId, shapeIndex) => {
                // 如果 timeNodeType 不存在，则默认为第一个 shape 为 clickEffect，其余为 withEffect
                if (!nodeType) {
                    nodeType = shapeIndex === 0 ? STTLTimeNodeType.clickEffect : STTLTimeNodeType.withEffect;
                }
                const addMut = this.createAddAnimationMutation(shapeId, triggerId, shapeIndex + index);
                const setMut = this.createSetAnimationMutation(shapeId, triggerId, shapeIndex + index, {
                    presetSubtype,
                    animType,
                    nodeType,
                    duration,
                    delay,
                    mediaType,
                });
                addMut && setMut && this.transaction(() => this.request([addMut, setMut]));
            });
        });
    }

    @record()
    public removeAnimation(triggerId: string, index: number) {
        const removeAnimationMut = this.createRemoveAnimation(triggerId, index);
        const setAnimationMut = this.createSetAnimationMutation(
            removeAnimationMut.toData()?.shapeId ?? '',
            triggerId,
            index,
            {},
        );
        this.transaction(() => this.request([setAnimationMut, removeAnimationMut]));
    }

    @record()
    public removeAnimationsByShapeId(shapeId: string) {
        if (!this.getPresentation().store.models.get(shapeId, ...SHAPE_DATA_TYPES)) return;
        this.transaction(() => {
            Object.keys(this.model.props.unitTimeNodeMap).forEach((triggerId) => {
                // 媒体资源原始文件不走删除接口，会通过引用计数自动删除
                if (triggerId.startsWith(MEDIA_TRIGGER_ID_PREFIX)) return;
                const item = this.model.props.unitTimeNodeMap[triggerId];
                // 移除 shape 自身的动画
                [...(item?.unitTimeNodeList ?? [])].forEach((unitTimeNodeData) => {
                    const unitTimeNode = new UnitTimeNode(unitTimeNodeData);
                    if (unitTimeNode.shapeId === shapeId) {
                        // removeAnimation 是直接执行的，需要每次重新计算 removeIndex
                        const removeIndex =
                            this.model.props.unitTimeNodeMap[triggerId]?.unitTimeNodeList.indexOf(unitTimeNodeData) ??
                            0;
                        this.removeAnimation(triggerId, removeIndex);
                    }
                });
                // 存在自己触发自己的动画，上面删除自身动画后，触发器动画可能就为空了
                const curTriggerList = this.model.props.unitTimeNodeMap[triggerId]?.unitTimeNodeList;
                // 将把 shape 作为触发器的动画移动到主动画队列
                if (triggerId === shapeId && curTriggerList?.length) {
                    let index = curTriggerList.length - 1;
                    const toIndex = this.model.getSeqByTriggerId(K_DEFAULT_TRIGGER_ID)?.unitTimeNodeList.length ?? 0;
                    while (index >= 0) {
                        this.moveAnimation(index, toIndex, triggerId, K_DEFAULT_TRIGGER_ID);
                        index -= 1;
                    }
                }
            });
        });
    }

    /**
     * 移动动画
     * @param fromIndex 移动前动画在队列中的索引
     * @param index 移动后动画在队列中的索引
     * @param fromTriggerId 移动前的动画所属队列的触发器元素 id
     * @param triggerId 移动后的动画所属队列的触发器元素 id
     */
    @record()
    public moveAnimation(
        fromIndex: number,
        index: number,
        fromTriggerId = K_DEFAULT_TRIGGER_ID,
        triggerId = K_DEFAULT_TRIGGER_ID,
    ) {
        const fromItem = this.model.props.unitTimeNodeMap[fromTriggerId];
        const desItem = this.model.props.unitTimeNodeMap[triggerId];
        const unitTimeNodeData: DeepReadonly<IEGTimeNodeChoice> | undefined = fromItem?.unitTimeNodeList?.[fromIndex];
        assert(fromItem && unitTimeNodeData, 'time node not found');
        const unitTimeNode = new UnitTimeNode(unitTimeNodeData);
        this.transaction(() => {
            this.request([
                new MoveAnimationMutation({
                    pageId: this.context.getPage().id,
                    shapeId: unitTimeNode.shapeId ?? '',
                    fromTriggerId,
                    triggerId,
                    fromIndex,
                    index,
                    fromSeqType: fromItem?.type,
                    seqType:
                        desItem?.type ??
                        (triggerId === K_DEFAULT_TRIGGER_ID
                            ? STTLTimeNodeType.mainSeq
                            : STTLTimeNodeType.interactiveSeq),
                }),
            ]);
        });
    }

    /**
     * 修改/填充动画数据
     * @param triggerId 修改的动画所属队列的触发器元素 id
     * @param index 动画在队列中的索引
     * @param animModifyParam 动画修改参数
     */
    @record()
    public modifyAnimation(triggerId: string, index: number, animModifyParam: IAnimModifyParam) {
        const item = this.model.props.unitTimeNodeMap[triggerId];
        const unitTimeNodeData = item?.unitTimeNodeList?.[index] as IEGTimeNodeChoice;
        assert(!!unitTimeNodeData, 'time node not found');
        const { shapeId, dur, delay, nodeType, animType, presetSubtype, by, opacity, presetClass, id, color } =
            new UnitTimeNode(unitTimeNodeData);
        const isSupportEditAnimType = this.model.store.isSupportEditAnimType(unitTimeNodeData);
        const shape = this.getPresentation()?.store.models.get(shapeId ?? '', ...SHAPE_DATA_TYPES);
        assert(shapeId && shape?.id === shapeId, 'shapeId should be exist');
        const toTriggerId = animModifyParam.triggerId || triggerId;
        const isTriggerIdChanged = triggerId !== toTriggerId;
        const newAnimType = animModifyParam.animType || animType;
        const isAnimTypeChanged = animType !== newAnimType;
        this.transaction(() => {
            if (isAnimTypeChanged) {
                this.request([
                    this.createSetAnimationMutation(
                        shapeId,
                        triggerId,
                        index,
                        { id, nodeType, shapeId, triggerId, ...animModifyParam },
                        getMediaParams(this.model.getTimeNode.bind(this.model), shapeId, presetClass),
                    ),
                ]);
                return;
            }
            const unitTimeNodeParams = {
                shapeId,
                id,
                dur,
                delay,
                nodeType,
                animType,
                presetSubtype,
                by,
                opacity,
                triggerId,
                color,
                ...animModifyParam,
            };
            // 如果 triggerId 发生变化，先保持不变，通过调用 moveAnimation 来修改 triggerId
            this.request([
                // 如果是不支持编辑的动画类型，需要使用原始数据进行创建mutation
                isSupportEditAnimType
                    ? this.createSetAnimationMutation(
                          shapeId,
                          triggerId,
                          index,
                          isTriggerIdChanged ? { ...unitTimeNodeParams, triggerId } : unitTimeNodeParams,
                          getMediaParams(this.model.getTimeNode.bind(this.model), shapeId, presetClass),
                      )
                    : this.createUnSupportSetAnimationMutation(
                          shapeId,
                          triggerId,
                          index,
                          isTriggerIdChanged ? { ...unitTimeNodeParams, triggerId } : unitTimeNodeParams,
                          cloneDeep(unitTimeNodeData),
                      ),
            ]);
            if (isTriggerIdChanged) {
                const toIndex = this.model.getSeqByTriggerId(toTriggerId)?.unitTimeNodeList.length || 0;
                this.moveAnimation(index, toIndex, triggerId, toTriggerId);
            }
        });
    }

    /**
     * 修改媒体 origin (media_xxxx)动画数据
     * 该类数据无法走正常 modifyAnimation 流程( referenceCount 可能会有坑)，需要走添加删除旧节点、添加新节点的流程以达到修改目的
     * @param triggerId
     * @param spid
     * @param index 动画在主队列中的索引
     * @param mediaAnimModifyParam 动画修改参数
     */
    @record()
    public modifyOriginMediaAnimation(
        triggerId: string,
        spid: string,
        index: number,
        mediaAnimModifyParam: { audio?: IOriginMediaAnimModifyParam; video?: IOriginMediaAnimModifyParam },
    ) {
        const matchedTriggerItemList = this.model.store.data.unitTimeNodeMap[triggerId]?.unitTimeNodeList;
        const matchedTriggerUnitNodeData = matchedTriggerItemList?.[index];
        assert(matchedTriggerUnitNodeData, 'No matched UnitNode');

        // 根据 spid 寻找 media_xxxxx 对应节点
        const presetClass = UnitTimeNode.getPresetClass(matchedTriggerUnitNodeData);
        assert(spid, 'shapeId should be exist');
        const mediaItemList = this.model.store.data.unitTimeNodeMap[MEDIA_TRIGGER_ID_PREFIX + spid]?.unitTimeNodeList;
        const mediaUnitNodeData: IEGTimeNodeChoice | undefined = mediaItemList?.[0];
        assert(mediaUnitNodeData, 'mediaUnitNode should be exist');

        // 根据 spid 获取到本次修改中被涉及到的所有 触发动画 节点
        const triggerItemList = this.model.store.data.unitTimeNodeMap[spid]?.unitTimeNodeList;
        const relativeTriggerNodes: { index: number; node: UnitTimeNode }[] = [];
        triggerItemList?.forEach((nodeData, itemIndex) => {
            if (UnitTimeNode.getShapeId(nodeData) === spid && UnitTimeNode.getPresetClass(nodeData) === presetClass) {
                // 默认为 media_xxxxx 第1个节点
                mediaUnitNodeData.par = cloneDeep(nodeData?.par); // 添加 par 属性
                const mediaTimeNode = new UnitTimeNode(mediaUnitNodeData || {});
                mediaTimeNode.updateDataExpectAnimType(mediaAnimModifyParam);
                relativeTriggerNodes.push({ index: itemIndex, node: mediaTimeNode });
            }
        });

        // 获取到本次修改中被涉及到的所有 kDefault 动画节点
        const kDefaultItemList = this.model.store.data.unitTimeNodeMap[K_DEFAULT_TRIGGER_ID]?.unitTimeNodeList;
        const relativeKDefaultNodes: { index: number; node: UnitTimeNode }[] = [];
        kDefaultItemList?.forEach((nodeData, itemIndex) => {
            if (UnitTimeNode.getShapeId(nodeData) === spid && UnitTimeNode.getPresetClass(nodeData) === presetClass) {
                mediaUnitNodeData.par = cloneDeep(nodeData?.par); // 添加 par 属性
                const mediaTimeNode = new UnitTimeNode(mediaUnitNodeData || {});
                mediaTimeNode.updateDataExpectAnimType(mediaAnimModifyParam);
                relativeKDefaultNodes.push({ index: itemIndex, node: mediaTimeNode });
            }
        });

        this.transaction(() => {
            const addMuts: AddAnimationMutation[] = [];
            const setMuts: SetAnimationMutation[] = [];

            /**
             * 如果不将所有相关节点删除干净，则会设置失败
             * 具体原因在： timing-store 中 setMediaData 方法的 Object.assign 一步中
             * todo：后期配合中台修改。
             */
            relativeTriggerNodes.forEach(({ index: relativeNodeIndex }, cycleIndex) => {
                // 移除所有相关的 触发动画 旧节点
                this.removeAnimation(spid, relativeNodeIndex - cycleIndex);
                // 将删除的旧节点重新添加回来
                addMuts.push(this.createAddAnimationMutation(spid, spid, relativeNodeIndex));
            });

            relativeKDefaultNodes.forEach(({ node, index: relativeNodeIndex }, cycleIndex) => {
                // 移除所有相关的 kDefaultTriggerId 旧节点
                this.removeAnimation(K_DEFAULT_TRIGGER_ID, relativeNodeIndex - cycleIndex);
                // 将删除的旧节点重新添加回来
                addMuts.push(this.createAddAnimationMutation(spid, K_DEFAULT_TRIGGER_ID, relativeNodeIndex));
                // 重新设置 kDefault 节点
                setMuts.push(
                    new SetAnimationMutation({
                        index: relativeNodeIndex,
                        shapeId: spid,
                        triggerId: K_DEFAULT_TRIGGER_ID,
                        timeNode: node.getData(),
                        pageId: this.context.getPage().id,
                        seqType: STTLTimeNodeType.mainSeq,
                    }),
                );
            });

            // 重新设置 触发动画 节点
            relativeTriggerNodes.forEach(({ node, index: relativeNodeIndex }) => {
                setMuts.push(
                    new SetAnimationMutation({
                        index: relativeNodeIndex,
                        shapeId: spid,
                        triggerId: spid,
                        timeNode: node.getData(),
                        pageId: this.context.getPage().id,
                        seqType: STTLTimeNodeType.interactiveSeq,
                    }),
                );
            });

            this.request([...addMuts, ...setMuts]);
        });
    }

    /**
     * 创建添加动画的mutation
     * @param shapeDataModel 添加动画的shape
     * @param triggerId 添加动画所属队列的触发器元素 id
     * @param index 添加动画在队列中的索引
     */
    private createAddAnimationMutation(shapeId: string, triggerId: string, index: number) {
        return new AddAnimationMutation({
            pageId: this.context.getPage().id,
            shapeId,
            triggerId,
            seqType: triggerId === K_DEFAULT_TRIGGER_ID ? STTLTimeNodeType.mainSeq : STTLTimeNodeType.interactiveSeq,
            index,
        });
    }

    /**
     * 创建不支持编辑的动画的setAnimationMutation
     * 目前不支持编辑的动画，仅限制不支持编辑动画类型
     * @param shapeId
     * @param triggerId
     * @param index
     * @param optionalParams
     * @param timeNode
     * @returns
     */
    private createUnSupportSetAnimationMutation(
        shapeId: string,
        triggerId: string,
        index: number,
        optionalParams: DeepPartial<IAnimModifyParam>,
        timeNode: IEGTimeNodeChoice,
    ) {
        const unitTimeNode = new UnitTimeNode(timeNode);
        unitTimeNode.updateDataExpectAnimType({ shapeId, ...optionalParams });
        return new SetAnimationMutation({
            pageId: this.context.getPage().id,
            shapeId,
            triggerId,
            seqType: STTLTimeNodeType.mainSeq,
            index,
            timeNode,
        });
    }

    /**
     * 创建设置动画的mutation
     * @param shapeDataModel 设置动画的shape
     * @param triggerId 设置动画所属队列的触发器元素 id
     * @param index 设置动画在队列中的索引
     * @param optionalParams 设置动画的可选参数
     */
    private createSetAnimationMutation(
        shapeId: string,
        triggerId: string,
        index: number,
        optionalParams: DeepPartial<IAnimModifyParam>,
        mediaParams: { audio?: ICTTLMediaNodeAudio; video?: ICTTLMediaNodeVideo } = {},
    ) {
        const unitTimeNode = new UnitTimeNode({ ...mediaParams });
        unitTimeNode.updateData({ shapeId, ...optionalParams });
        return new SetAnimationMutation({
            index,
            shapeId,
            triggerId,
            timeNode: unitTimeNode.getData(),
            pageId: this.context.getPage().id,
            seqType: triggerId === K_DEFAULT_TRIGGER_ID ? STTLTimeNodeType.mainSeq : STTLTimeNodeType.interactiveSeq,
        });
    }

    /**
     * 删除动画
     * @param triggerId 删除的动画所属队列的触发器元素 id
     * @param index 删除的动画在队列中的索引
     */
    private createRemoveAnimation(triggerId: string, index: number) {
        const item = this.model.props.unitTimeNodeMap[triggerId];
        const unitTimeNodeData: DeepReadonly<IEGTimeNodeChoice> | undefined = item?.unitTimeNodeList?.[index];
        assert(item && unitTimeNodeData, 'time node not found');
        const unitTimeNode = new UnitTimeNode(unitTimeNodeData);
        return new RemoveAnimationMutation({
            pageId: this.context.getPage().id,
            shapeId: unitTimeNode.shapeId ?? '',
            triggerId,
            seqType: item.type,
            index,
        });
    }
}
